import React, { useState, useEffect } from "react";
import ReactTable from "react-table";
import { Button, UncontrolledTooltip } from "reactstrap";
import s from "./styles.scss";
import TotalPassDetalle from "../../../types/TotalPassDetalle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faFileExport,
  faInfoCircle,
  faPencilAlt
} from "@fortawesome/free-solid-svg-icons";

export interface TableProps {
  page: Page<TotalPassDetalle>;
  loading: boolean;
  onFetchData: ({ page, pageSize }) => void;
  onExport: () => void;
  onEditClick: (idPersona) => void;
  onViewPersonaClick: (idPersona) => void;
}

export const MemoTable = React.memo(
  ({
    page,
    loading,
    onFetchData,
    onExport,
    onEditClick,
    onViewPersonaClick
  }: TableProps) => {
    // State de las columnas de la tabla
    const [columns, setColumns] = useState(
      columnsFormatter(onEditClick, onViewPersonaClick, loading)
    );

    const [pageNumber, setPageNumber] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);

    // Actualización de estilos
    useEffect(() => {
      setColumns(columnsFormatter(onEditClick, onViewPersonaClick, loading));
    }, []);

    // Actualización de datos de paginación
    useEffect(() => {
      if (page) {
        setPageNumber(page.number);
        setTotalPages(page.totalPages);
      }
    }, [page]);

    const mostrados = page && page.numberOfElements;
    const totales = page && page.totalElements;
    return (
      <>
        {/* Botón para exportar */}
        <div className="row">
          {!!page && !!page.content && !!page.content.length && (
            <>
              <div className="col-6 p-0" style={{ lineHeight: "40px" }}>
                <span>
                  Clientes mostrados: {mostrados} de {totales}
                </span>
              </div>
              <div
                className="col-6 p-0 text-right"
                style={{ lineHeight: "40px" }}
              >
                <Button
                  color="primary"
                  onClick={onExport}
                  disabled={loading}
                  className="mr-2"
                >
                  <FontAwesomeIcon icon={faFileExport} />
                  {!loading ? " Exportar" : " Espere..."}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Tabla */}
        <div className="row mt-2">
          <div style={{ width: "100%" }}>
            <ReactTable
              manual
              data={(page && page.content) || []}
              columns={columns}
              loading={loading}
              defaultPageSize={100}
              className="-striped -highlight"
              onFetchData={onFetchData}
              page={pageNumber}
              pages={totalPages}
              showPagination={true}
              showPageSizeOptions={false}
              getTrProps={() => ({ style: { height: "30px" } })}
              sortable={false}
            />
          </div>
        </div>
      </>
    );
  }
);

// TODO: Definir permisos para los botones
function columnsFormatter(
  onEditClick: (idPersona) => void,
  onViewPersonaClick: (idPersona) => void,
  loading: boolean
) {
  return [
    {
      Header: "ID",
      id: "idPersona",
      className: s.tdClear,
      accessor: simpleAccessor("idPersona")
    },
    {
      Header: "Nombre",
      id: "nombreCompleto",
      className: s.tdClear,
      accessor: simpleAccessor("nombreCompleto")
    },
    {
      Header: "Correo",
      id: "correo",
      className: s.tdClear,
      accessor: simpleAccessor("correo")
    },
    {
      Header: "Membresía",
      id: "membresia",
      className: s.tdClear,
      accessor: (row: TotalPassDetalle) => {
        return !!row.idMembresiaSocio ? (
          <div className={`${fieldClassName(row)}`}>
            <a
              hidden={!row.idPublico}
              href="#"
              onClick={e => {
                e.preventDefault();
                window.open(
                  `/membresias-socios?idClub=${row.idClubBase}&idPublico=${row.idPublico}`,
                  "_blank"
                );
              }}
            >
              `${row.idPublico} de ${row.club}`
            </a>
          </div>
        ) : (
          <div className={`${fieldClassName(row)}`}>Sin membresía</div>
        );
      }
    },
    {
      id: "acciones",
      className: s.tdClear,
      width: 197,
      accessor: (row: TotalPassDetalle) => {
        if (row.vetado || row.eliminado) {
          return (
            <div
              className={`${fieldClassName(row)} text-center`}
              style={{
                color: "#d32f2f",
                fontWeight: "bold",
                fontSize: "12px",
                backgroundColor: "#ffebee",
                padding: "8px 4px",
                borderRadius: "4px",
                border: "1px solid #ffcdd2"
              }}
            >
              {row.vetado ? "CLIENTE EN BLACK LIST" : "CLIENTE ELIMINADO"}
            </div>
          );
        }

        const idPrefijo = "id-" + row.idPersona;
        const idAcceso = idPrefijo + "-acceso";
        const idVerPersona = idPrefijo + "-ver-persona";
        const idEditar = idPrefijo + "-editar";

        return (
          <div className={`${fieldClassName(row)} text-center`}>
            {row.tieneAcceso ? (
              <span className={s.info} id={idAcceso}>
                <FontAwesomeIcon icon={faInfoCircle} color="#008080" />
                <UncontrolledTooltip placement="left" target={idAcceso}>
                  El cliente tiene acceso
                </UncontrolledTooltip>
              </span>
            ) : (
              <span className={s.info} id={idAcceso}>
                <FontAwesomeIcon icon={faInfoCircle} color="#600" />
                <UncontrolledTooltip placement="left" target={idAcceso}>
                  {row.razonesSinAcceso.map(r => (
                    <>
                      {r}
                      <br />
                    </>
                  ))}
                </UncontrolledTooltip>
              </span>
            )}
            {
              <span
                onClick={() => onViewPersonaClick(row.idPersona)}
                className={s.btn}
                id={idVerPersona}
              >
                <FontAwesomeIcon icon={faEye} color="#c90" />
                <UncontrolledTooltip placement="left" target={idVerPersona}>
                  Ver persona
                </UncontrolledTooltip>
              </span>
            }
            {
              <span
                onClick={() => onEditClick(row.idPersona)}
                className={s.btn}
                id={idEditar}
              >
                <FontAwesomeIcon icon={faPencilAlt} color="#c90" />
                <UncontrolledTooltip placement="left" target={idEditar}>
                  Editar cliente
                </UncontrolledTooltip>
              </span>
            }
          </div>
        );
      }
    }
  ];
}

function simpleAccessor(key: keyof TotalPassDetalle) {
  return (row: TotalPassDetalle) => {
    return <div className={fieldClassName(row)}>{row[key]}&nbsp;</div>;
  };
}

function fieldClassName(row: TotalPassDetalle) {
  return row.tieneAcceso ? `${s.field}` : `${s.field} ${s.sinAcceso}`;
}
